/**
 * The host for a column type's settings dialog (spec M1-B5 §6.2).
 *
 * The dialog itself is owned by the type folder (`ColumnTypeUi.SettingsDialog`) — status
 * labels, dropdown options, number format, date settings. This host is the only thing that
 * knows *where* it opens, how it saves and what happens when the type has no dialog, so
 * adding one to a new type stays a single-folder change (CLAUDE.md rule 6).
 *
 * It is opened from two places: the column header menu, and the `✎ Edit Labels` row inside a
 * cell editor, which reaches it through `columns/settings-request.ts` (parity card `P-003`).
 */
import type { ColumnId } from '@elizmonday/shared';
import { useEffect, useState } from 'react';
import { getColumnUi } from '../columns/registry';
import { useTable, useTableActions } from './context';

export function ColumnSettingsHost({
  columnId,
  onClose,
}: {
  columnId: ColumnId;
  onClose(): void;
}) {
  const column = useTable((state) => state.payload?.columns.find((entry) => entry.id === columnId));
  /*
   * `progress` names the status columns it tracks and `mirror` the relation it follows, so the
   * dialog family is handed the board's columns exactly as the cell renderers are
   * (`ColumnSettingsProps.columns`, card P-123). Nothing here branches on the type.
   */
  const columns = useTable((state) => state.payload?.columns);
  const actions = useTableActions();
  const [draft, setDraft] = useState<unknown>(column?.settings ?? null);

  useEffect(() => {
    setDraft(column?.settings ?? null);
  }, [column?.settings]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  if (!column) return null;
  const Dialog = getColumnUi(column.type).SettingsDialog;
  if (!Dialog) return null;

  return (
    <div className="ez-column-settings-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-column-settings"
        role="dialog"
        aria-modal="true"
        aria-label={`${column.title} settings`}
        data-testid="column-settings-dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Dialog
          column={column}
          settings={draft}
          columns={columns ?? []}
          revision={column.revision}
          onChange={setDraft}
          /*
           * **P-129.** This used to be `void actions.updateColumnSettings(...)` followed by an
           * unconditional `onClose()`: the dialog closed the instant Save was pressed, whatever
           * the server said, so a refused write looked exactly like a successful one. The host
           * now waits for the answer and closes **only on success** — a failure leaves the
           * dialog open with the draft intact, over the toast the store raised.
           */
          onCommit={(next) => {
            void (async () => {
              if (await actions.updateColumnSettings(columnId, next)) onClose();
            })();
          }}
          onCancel={onClose}
        />
      </div>
    </div>
  );
}
